import { Entity, EntityRepositoryType, Index, PrimaryKey, Property, Unique } from '@mikro-orm/postgresql'
import { EntityRepository } from '@mikro-orm/postgresql'
import { v4 as uuid } from 'uuid'
import { BaseEntity } from '../core/core.entities'

// structured permissions per the PEAK-50975 concept model (docs/PEAK-50975.md)
export type RevenueDetailReportScope = {
	salesChannels: string[]
	regions: string[]
	serviceProviders: string[]
	products: string[]
	resellers: string[]
}

export type OperatorPermissions = {
	twoFactorAuth: { enabled: boolean }
	myServices: { enabled: boolean, tags: string[] }
	serviceDesigner: { enabled: boolean }
	contentHub: { enabled: boolean, role?: string, roleName?: string }
	reportCenter: { enabled: boolean, revenueDetailReport?: RevenueDetailReportScope }
	// username is PEAK's derived backoffice login (≤40 chars, returned by createUser) —
	// the Metis login flow resolves sessions by username, not e-mail
	peaksolution: { enabled: boolean, role?: string, username?: string }
	// MYS-7471: derived from the Flexible Pricing Engine app in the operator's application list.
	// domainId (the PS3 domain last provisioned) and ps3UserId are server-managed
	flexiblePricing?: { enabled: boolean, domainId?: string, ps3UserId?: string }
}

export function defaultOperatorPermissions(): OperatorPermissions {
	return {
		twoFactorAuth: { enabled: false },
		myServices: { enabled: true, tags: [] },
		serviceDesigner: { enabled: false },
		contentHub: { enabled: false },
		reportCenter: { enabled: false },
		peaksolution: { enabled: false },
	}
}

// per-target-system provisioning outcome — partner systems are best-effort with retry.
// keycloak/contentManager only carry states during DELETION (they are mandatory on create)
export type ProvisioningSystem = 'contentHub' | 'reportCenter' | 'peaksolution' | 'metis' | 'flexiblePricing' | 'keycloak' | 'contentManager'

// machine-readable failure reasons the UI reacts to (the error text is for humans only)
export type ProvisioningErrorCode = 'noBillingDomain'

export type ProvisioningState = {
	status: 'pending' | 'provisioned' | 'failed'
	error?: string
	code?: ProvisioningErrorCode
	updatedAt: string
}

export type ProvisioningStatusMap = Partial<Record<ProvisioningSystem, ProvisioningState>>

// where a catalog entry came from: created by a human, seeded unambiguously from a
// single-client user, or seeded from a multi-client user (attribution unknown —
// Toolbox flags these for human verification)
export type TagOrigin = 'manual' | 'seed' | 'seed-multi-client'

class TagRepository extends EntityRepository<Tag> {}

// tags as first-class entities per client (PEAK-50975 / MYS-7144).
// enforcement still runs on the user string arrays; matching is by tag name.
@Entity({ repository: () => TagRepository, tableName: 'tag' })
@Unique({ properties: [ 'clientId', 'name' ] })
export class Tag extends BaseEntity {
	[EntityRepositoryType]?: TagRepository

	@PrimaryKey()
	id: string = uuid()

	@Property()
	clientId: string

	@Property()
	name: string

	@Property({ nullable: true })
	color?: string

	@Property({ nullable: true })
	origin?: TagOrigin

	constructor(clientId: string, name: string, origin?: TagOrigin, color?: string) {
		super()
		this.clientId = clientId
		this.name = name
		this.origin = origin
		this.color = color
	}
}

class UserPermissionsRepository extends EntityRepository<UserPermissions> {}

@Entity({ repository: () => UserPermissionsRepository, tableName: 'user_permissions' })
export class UserPermissions extends BaseEntity {
	[EntityRepositoryType]?: UserPermissionsRepository

	@PrimaryKey()
	id: string = uuid()

	// CM user entry id — users themselves stay in Content Manager
	@Property()
	@Unique()
	userId: string

	@Property()
	@Index({ name: 'user_permissions_client_id_index' })
	clientId: string

	@Property({ nullable: true })
	keycloakId?: string

	// mirror of the Keycloak enabled flag so the list view needs no per-user KC lookup;
	// Keycloak stays the source of truth (written through setOperatorEnabled)
	@Property({ default: true })
	enabled: boolean = true

	@Property({ type: 'json' })
	permissions: OperatorPermissions = defaultOperatorPermissions()

	@Property({ type: 'json' })
	provisioning: ProvisioningStatusMap = {}

	// deletion in progress: the row survives until every system is deprovisioned so a
	// retried delete knows the remaining work; failures live in `provisioning`
	@Property({ default: false })
	markedForDeletion: boolean = false

	constructor(userId: string, clientId: string, keycloakId?: string) {
		super()
		this.userId = userId
		this.clientId = clientId
		this.keycloakId = keycloakId
	}
}
